/**
 * The ⓘ affordance every drawer can carry.
 *
 * A drawer's title says *what* it is ("Merge", "Transfer"); it has nowhere to
 * say what the action will actually do, which fields matter, or what can't be
 * undone — so that knowledge lived in people's heads. Passing `info` to a
 * drawer adds an ⓘ button beside its close button; pressing it expands a panel
 * under the header with the details, and pressing it again collapses it. It
 * starts collapsed and resets on close, so it never gets in the way of an agent
 * who already knows the flow.
 *
 * Structured rather than free-form ReactNode on purpose: every drawer's help
 * then reads the same way — one lead sentence, term/detail pairs, and an
 * optional caution for the irreversible part.
 */
import { useEffect, useState, type JSX } from 'react'
import { Info, X } from 'lucide-react'

export interface DrawerInfoContent {
  /** One sentence answering "what is this drawer for?". */
  lead: string
  /** Field or option → what it does. Keep each detail to a sentence. */
  points?: Array<{ term: string; detail: string }>
  /** Anything irreversible or easily missed. Rendered as a warning line. */
  caution?: string
}

/** Open/close state for a drawer's info panel, reset whenever the drawer
 *  closes so reopening it always starts from the form. */
export function useDrawerInfo(open: boolean): {
  shown: boolean
  toggle: () => void
} {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    if (!open) setShown(false)
  }, [open])
  return { shown, toggle: () => setShown((v) => !v) }
}

/** Header button that reveals the info panel. Render next to the close button. */
export function DrawerInfoButton({
  shown,
  onToggle,
  className = '',
}: {
  shown: boolean
  onToggle: () => void
  className?: string
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={shown}
      aria-label={shown ? 'Hide details about this panel' : 'About this panel'}
      title={shown ? 'Hide details' : 'About this panel'}
      className={`flex h-8 w-8 items-center justify-center rounded-[3px] transition-colors ${
        shown
          ? 'bg-[#EEF4FF] text-[#3366FF]'
          : 'text-gray-400 hover:bg-gray-100 hover:text-[#3366FF]'
      } ${className}`}
    >
      <Info className="h-4 w-4" />
    </button>
  )
}

/** The panel itself. Sits between the drawer header and its body, and scrolls
 *  on its own so long help can never push the form out of reach. */
export function DrawerInfoPanel({
  info,
  shown,
  onDismiss,
}: {
  info: DrawerInfoContent
  shown: boolean
  onDismiss: () => void
}): JSX.Element | null {
  if (!shown) return null
  return (
    <div className="max-h-64 shrink-0 overflow-y-auto border-b border-[#C9D9FB] bg-[#F5F8FF] px-5 py-4 text-xs leading-relaxed text-[#16273F]">
      <div className="flex items-start gap-2">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#3366FF]" />
        <p className="flex-1 font-semibold">{info.lead}</p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Hide details"
          className="-mr-1 -mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-[3px] text-gray-400 transition-colors hover:bg-white hover:text-gray-600"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {info.points && info.points.length > 0 && (
        <dl className="mt-2.5 space-y-1.5 pl-5">
          {info.points.map((p) => (
            <div key={p.term} className="flex flex-wrap gap-x-1.5">
              <dt className="font-semibold text-[#16273F]">{p.term}</dt>
              <dd className="flex-1 text-gray-600">{p.detail}</dd>
            </div>
          ))}
        </dl>
      )}
      {info.caution && (
        <p className="mt-2.5 pl-5 font-medium text-red-600">{info.caution}</p>
      )}
    </div>
  )
}
